import { useMemo, useCallback, memo } from 'react'
import { CategoriesBaseProps, CategoryItem, defaultCategories, categoryColors, formatChange } from './CategoriesBase'
import { ProjectLogo } from './ProjectLogo'
import { InfoTooltip } from '@/components/ui/info-tooltip'

const CategoryItemComponent = memo(({ 
  item, 
  index, 
  onItemClick 
}: { 
  item: CategoryItem
  index: number
  onItemClick?: (item: CategoryItem) => void 
}) => {
  // 🔴 `item.change` has never been populated by any caller, and `|| 0` turned
  // that into a confident "+0%" on every row — a price change nobody measured,
  // rendered in the same type as a real one. Show the chip only when there is
  // a figure to show.
  const change = item.change
  const color = categoryColors[index % categoryColors.length]
  
  const handleClick = useCallback(() => {
    onItemClick?.(item)
  }, [item, onItemClick])
  
  const changeText = change === undefined ? null : formatChange(change)
  
  return (
    <div
      className="bg-white dark:bg-[#1c1c1c] flex items-center justify-between pl-3 pr-[18px] py-3 cursor-pointer hover:opacity-90 transition-opacity"
      onClick={handleClick}
    >
      <div className="flex gap-[10px] items-start">
        <ProjectLogo 
          logo={item.logo} 
          alt={item.project_name || 'Project'}
          color={color}
        />
        <div className="flex items-baseline">
          <p 
            className="font-plus-jakarta font-medium leading-[24px] text-[#2d2923] dark:text-[#fdfdfd] text-base overflow-hidden text-ellipsis whitespace-nowrap"
            
          >
            {item.project_name || 'Project'}
          </p>
        </div>
      </div>
      {changeText !== null && (
        <div className="flex flex-col justify-center overflow-hidden text-ellipsis whitespace-nowrap">
          <p
            className="font-plus-jakarta font-semibold leading-[20px] text-[14px] text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)] overflow-hidden text-ellipsis whitespace-nowrap"
          >
            {changeText}
          </p>
        </div>
      )}
    </div>
  )
})

CategoryItemComponent.displayName = 'CategoryItemComponent'

export const CategoriesMobile = ({ 
  items, 
  categories = defaultCategories,
  selectedCategory = '',
  onCategoryChange,
  onItemClick,
  loading = false
}: CategoriesBaseProps) => {
  const filteredItems = useMemo(() => {
    return selectedCategory 
      ? items.filter(item => item.category === selectedCategory || item.industry === selectedCategory)
      : items
  }, [items, selectedCategory])

  // Filter categories to only show those with orders
  const categoriesWithOrders = useMemo(() => {
    const allCategories = categories.length > 0 ? categories : defaultCategories
    return allCategories.filter(category => {
      return items.some(item => item.category === category || item.industry === category)
    })
  }, [categories, items])

  const displayCategories = categoriesWithOrders
  const activeCategory = (selectedCategory && displayCategories.includes(selectedCategory)) 
    ? selectedCategory 
    : displayCategories[0] || ''

  if (loading) {
    return (
      <div className="h-full flex flex-col relative px-6 pt-6 pb-6">
        <p 
          className="font-plus-jakarta font-medium leading-[24px] text-[#2d2923] dark:text-[#fdfdfd] text-base uppercase"
          
        >
          <span className="inline-flex items-center gap-2">
            Categories
            <InfoTooltip content="Browse Secondaries orders by industry/category." ariaLabel="Categories info" />
          </span>
        </p>
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)] text-sm">
            Loading categories...
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="h-full flex flex-col px-6 pt-6 pb-6">
      <div className="flex items-center mb-6">
        <p className="font-plus-jakarta font-medium leading-[24px] text-[#2d2923] dark:text-[#fdfdfd] text-base uppercase">
          <span className="inline-flex items-center gap-2">
            Categories
            <InfoTooltip content="Browse Secondaries orders by industry/category." ariaLabel="Categories info" />
          </span>
        </p>
      </div>

      <div className="mb-4 overflow-x-auto scrollbar-hide">
        <div className="flex items-center gap-[20px] min-w-max">
          {displayCategories.map((category) => {
            const isActive = category === activeCategory

            return (
              <button
                key={category}
                onClick={() => onCategoryChange?.(category)}
                className={`pb-[8px] pt-[4px] leading-[20px] text-[14px] whitespace-nowrap border-b ${
                  isActive
                    ? 'border-[#2d2923] dark:border-[#fdfdfd] text-[#2d2923] dark:text-[#fdfdfd] font-semibold'
                    : 'border-transparent text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)] font-medium'
                }`}
              >
                {category}
              </button>
            )
          })}
        </div>
      </div>

      <div className="flex-1 space-y-3">
        {filteredItems.map((item, index) => (
          <CategoryItemComponent
            key={item.order_id || item.project_id || `${item.project_name}-${index}`}
            item={item}
            index={index}
            onItemClick={onItemClick}
          />
        ))}

        {filteredItems.length === 0 && (
          <div className="text-center py-8 text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)] text-sm">
            No items in this category
          </div>
        )}
      </div>
    </div>
  )
}
